import React, { useMemo, useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";
import { Flame, Clock, CheckCircle2, Heart, TrendingUp, Sparkles, Loader2, Timer, Hourglass } from "lucide-react";
import { DAYS, SLOTS, API, axios, answerStatusOf } from "../../lib/planner";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-xl border border-[#E5E1D8] bg-white p-4" data-testid={testid}>
    <div className="mb-2 flex items-center gap-1.5 text-ink-muted">
      <Icon className="h-3.5 w-3.5" />
      <span className="font-sans text-xs uppercase tracking-[0.1em]">{label}</span>
    </div>
    <p className="font-serif text-3xl leading-none text-ink">{value}</p>
    {sub && <p className="mt-1 font-sans text-xs text-ink-muted">{sub}</p>}
  </div>
);

export const InsightsSheet = ({ open, onOpenChange, entries, settings, stats, weekKey, onFilterStatus }) => {
  const [reflection, setReflection] = useState("");
  const [reflectionLoading, setReflectionLoading] = useState(false);
  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    if (!open) { setChartReady(false); return; }
    const t = setTimeout(() => setChartReady(true), 360);
    return () => clearTimeout(t);
  }, [open]);

  const generateReflection = async () => {
    setReflectionLoading(true);
    try {
      const res = await axios.post(`${API}/ai/weekly-reflection`, { week_key: weekKey });
      setReflection(res.data.reflection);
    } catch (e) {
      toast.error("Could not generate reflection");
    } finally {
      setReflectionLoading(false);
    }
  };

  const completed = useMemo(() => entries.filter((e) => e.completed), [entries]);
  const minutes = useMemo(() => completed.reduce((s, e) => s + (e.duration_minutes || 0), 0), [completed]);
  const completionRate = entries.length ? Math.round((completed.length / entries.length) * 100) : 0;

  const chartData = useMemo(
    () =>
      DAYS.map((day) => ({
        day: day.slice(0, 3),
        minutes: completed.filter((e) => e.day === day).reduce((s, e) => s + (e.duration_minutes || 0), 0),
      })),
    [completed]
  );

  const slotBreakdown = useMemo(
    () =>
      SLOTS.map((slot) => ({
        label: slot.label,
        count: completed.filter((e) => e.time_slot === slot.key).length,
      })),
    [completed]
  );

  const journey = useMemo(() => {
    const c = { in_progress: 0, unanswered: 0, answered: 0 };
    entries.forEach((e) => { c[answerStatusOf(e)] = (c[answerStatusOf(e)] || 0) + 1; });
    return c;
  }, [entries]);

  const answered = stats?.answered_prayers || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-full overflow-y-auto border-l-[#E5E1D8] bg-clay-app p-6 sm:max-w-md"
        data-testid="insights-sheet"
      >
        <SheetHeader className="mb-6 text-left">
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-ink-muted">Reflections</p>
          <SheetTitle className="font-serif text-3xl font-medium text-ink">Your rhythm of prayer</SheetTitle>
          <SheetDescription className="sr-only">Weekly prayer statistics, streak, and answered prayers log</SheetDescription>
        </SheetHeader>

        <div className="grid grid-cols-2 gap-3">
          <Stat icon={Flame} label="Streak" value={`${stats?.current_streak ?? 0}`}
            sub={`Longest ${stats?.longest_streak ?? 0} days`} testid="insight-streak" />
          <Stat icon={Clock} label="Minutes" value={minutes} sub="This week" testid="insight-minutes" />
          <Stat icon={CheckCircle2} label="Sessions" value={completed.length}
            sub={settings ? `Target ${settings.target_value} ${settings.target_type === "minutes" ? "min" : ""}`.trim() : ""}
            testid="insight-sessions" />
          <Stat icon={TrendingUp} label="Completion" value={`${completionRate}%`} sub="Of planned" testid="insight-completion" />
        </div>

        <div className="mt-6 rounded-xl border border-[#E5E1D8] bg-clay-soft p-5" data-testid="reflection-card">
          <div className="mb-2 flex items-center gap-2 text-brand">
            <Sparkles className="h-4 w-4" />
            <p className="font-sans text-xs uppercase tracking-[0.1em] text-ink-muted">AI weekly reflection</p>
          </div>
          {reflection ? (
            <p className="font-serif text-xl italic leading-snug text-ink" data-testid="reflection-text">{reflection}</p>
          ) : (
            <p className="font-sans text-sm text-ink-soft">Let a gentle reflection summarise your week of prayer and celebrate answered prayers.</p>
          )}
          <button
            onClick={generateReflection}
            disabled={reflectionLoading}
            className="mt-3 flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-medium text-white transition-colors duration-300 hover:bg-brand-hover disabled:opacity-50"
            data-testid="generate-reflection-btn"
          >
            {reflectionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {reflection ? "Regenerate" : "Generate reflection"}
          </button>
        </div>

        <div className="mt-6 rounded-xl border border-[#E5E1D8] bg-white p-4">
          <p className="mb-3 font-sans text-xs uppercase tracking-[0.1em] text-ink-muted">Minutes prayed this week</p>
          <div className="h-40 min-h-[160px]">
            {chartReady ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <BarChart data={chartData} barCategoryGap="30%">
                  <XAxis dataKey="day" axisLine={false} tickLine={false}
                    tick={{ fill: "#8A9485", fontSize: 11, fontFamily: "Figtree" }} />
                  <Bar dataKey="minutes" radius={[6, 6, 0, 0]}>
                    {chartData.map((d) => (
                      <Cell key={d.day} fill="#556B4E" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full w-full animate-pulse rounded-lg bg-clay-panel" />
            )}
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-[#E5E1D8] bg-white p-4" data-testid="journey-card">
          <p className="mb-3 font-sans text-xs uppercase tracking-[0.1em] text-ink-muted">Prayer journey this week · tap to filter</p>
          <div className="grid grid-cols-3 gap-2">
            <button type="button" onClick={() => onFilterStatus("in_progress")} className="rounded-lg bg-brand/5 p-3 text-center transition-colors duration-300 hover:bg-brand/10" data-testid="journey-in_progress">
              <Timer className="mx-auto mb-1 h-4 w-4 text-brand" />
              <p className="font-serif text-2xl leading-none text-ink">{journey.in_progress}</p>
              <p className="mt-1 font-sans text-[11px] text-ink-muted">In progress</p>
            </button>
            <button type="button" onClick={() => onFilterStatus("unanswered")} className="rounded-lg bg-clay-panel p-3 text-center transition-colors duration-300 hover:bg-clay-soft" data-testid="journey-unanswered">
              <Hourglass className="mx-auto mb-1 h-4 w-4 text-ink-muted" />
              <p className="font-serif text-2xl leading-none text-ink">{journey.unanswered}</p>
              <p className="mt-1 font-sans text-[11px] text-ink-muted">Awaiting</p>
            </button>
            <button type="button" onClick={() => onFilterStatus("answered")} className="rounded-lg bg-clay-soft p-3 text-center transition-colors duration-300 hover:bg-[#e0d9c8]" data-testid="journey-answered">
              <Heart className="mx-auto mb-1 h-4 w-4 fill-[#C4A47C] text-[#C4A47C]" />
              <p className="font-serif text-2xl leading-none text-ink">{journey.answered}</p>
              <p className="mt-1 font-sans text-[11px] text-ink-muted">Answered</p>
            </button>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-[#E5E1D8] bg-white p-4">
          <p className="mb-3 font-sans text-xs uppercase tracking-[0.1em] text-ink-muted">By time of day</p>
          <div className="space-y-2">
            {slotBreakdown.map((s) => (
              <div key={s.label} className="flex items-center justify-between font-sans text-sm text-ink-soft">
                <span>{s.label}</span>
                <span className="font-serif text-lg text-ink">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <div className="mb-3 flex items-center gap-2">
            <Heart className="h-4 w-4 fill-[#C4A47C] text-[#C4A47C]" />
            <p className="font-serif text-xl text-ink">Answered prayers</p>
            <span className="font-sans text-sm text-ink-muted">({answered.length})</span>
          </div>
          {answered.length === 0 ? (
            <p className="rounded-xl border border-dashed border-[#E5E1D8] p-4 text-center font-sans text-sm text-ink-muted">
              Mark a prayer as answered to begin your gratitude log.
            </p>
          ) : (
            <div className="space-y-2" data-testid="answered-list">
              {answered.map((a) => (
                <div key={a.id} className="rounded-xl border border-[#E5E1D8] bg-white p-4" data-testid={`answered-${a.id}`}>
                  <p className="font-sans text-sm font-medium text-ink">{a.prayer_point || "Untitled prayer"}</p>
                  {a.answered_note && <p className="mt-1 font-serif text-base italic text-ink-soft">“{a.answered_note}”</p>}
                  <p className="mt-1 font-sans text-xs text-ink-muted">{a.day} · {a.week_key}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
